import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Spinner,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faHistory,
  faFilePdf,
  faFillDrip,
  faGlobe,
  faCheckCircle,
  faRedo,
} from "@fortawesome/free-solid-svg-icons";
import { useHistory, Route } from "react-router-dom";
import SavedItems from "./saved-items";
import "../common.css"; // Import common styles
import { competitorResearchPoints, marketResearchPoints } from "./data";
import { toast } from "react-toastify";
import { useRouteMatch } from "react-router-dom/cjs/react-router-dom.min";
import { mockData } from "./mockData";
import {
  generateAnalysis,
  downloadAnalysis,
} from "../../../service/workflow-api";

const labelToPascal = (text) => {
  return text
    .replace(/[^a-zA-Z0-9]+/g, "_")
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join("");
};

const CompetitorAndMarketResearch = () => {
  const [competitorItems, setCompetitorItems] = useState([
    ...competitorResearchPoints,
  ]);
  const [marketItems, setMarketItems] = useState([...marketResearchPoints]);
  const [draggedItems, setDraggedItems] = useState([]);
  const [formData, setFormData] = useState({});
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [researchResults, setResearchResults] = useState(null);

  const generateMockData = () => {
    setFormData(mockData);
  };

  const handleDragStart = (event, item, type, index) => {
    event.dataTransfer.setData(
      "item",
      JSON.stringify({
        ...item,
        originalType: type,
        originalIndex: index,
      })
    );
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    try {
      const droppedItem = JSON.parse(event.dataTransfer.getData("item"));

      // Find the input element associated with the dropped item
      const inputElement = document.querySelector(
        `[data-item-index="${droppedItem.originalIndex}"][data-item-type="${droppedItem.originalType}"]`
      );

      // Check if the input value is empty
      if (inputElement && inputElement.value.trim() === "") {
        toast.error("Please enter a value before adding to the plan.");
        return;
      }

      // Add to right panel
      setDraggedItems((prev) => [...prev, droppedItem]);

      // Remove from source panel
      if (droppedItem.originalType === "competitor") {
        setCompetitorItems((prev) =>
          prev.filter((_, i) => i !== droppedItem.originalIndex)
        );
      } else {
        setMarketItems((prev) =>
          prev.filter((_, i) => i !== droppedItem.originalIndex)
        );
      }
    } catch (error) {
      console.error("Error processing dropped item:", error);
    }
  };

  const handleRemove = (index) => {
    const itemToReturn = draggedItems[index];

    // Remove from right panel
    const newDraggedItems = [...draggedItems];
    newDraggedItems.splice(index, 1);
    setDraggedItems(newDraggedItems);

    // Return to original position in left panel
    if (itemToReturn.originalType === "competitor") {
      const newCompetitorItems = [...competitorItems];
      newCompetitorItems.splice(itemToReturn.originalIndex, 0, itemToReturn);
      setCompetitorItems(newCompetitorItems);
    } else {
      const newMarketItems = [...marketItems];
      newMarketItems.splice(itemToReturn.originalIndex, 0, itemToReturn);
      setMarketItems(newMarketItems);
    }
  };

  const handleHistoryClick = () => {
    history.push("/ai-workflows/ai-comp-market-research/saved-items");
  };

  const handleStartResearch = async () => {
    setLoading(true);
    setResearchResults(null);
    try {
      const items = {};
      draggedItems.forEach((i) => {
        items[labelToPascal(i.label)] = formData[labelToPascal(i.label)];
      });

      const response = await generateAnalysis(items);
      setResearchResults({ analysisId: response.analysis_id });
    } catch (error) {
      console.error("Error during research:", error);
      toast.error("Failed to generate analysis.");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReport = async (analysisId) => {
    try {
      const response = await downloadAnalysis(analysisId);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const htmlContent = data.html;

      // Create a new window with a meaningful title and URL
      const newWindow = window.open(" ", "_blank");

      if (newWindow) {
        newWindow.document.title = `Analysis Report ${analysisId}`;

        newWindow.document.open();
        newWindow.document.write(htmlContent);

        const printStyles = `
          <style type="text/css">
            @media print {
              .print-button {
                display: none !important;
              }
              @page {
                size: auto;
                margin: 20mm 10mm 20mm 10mm;
              }
            }
          </style>
        `;
        newWindow.document.head.insertAdjacentHTML("beforeend", printStyles);
        newWindow.document.close();

        newWindow.addEventListener("load", () => {
          const printButton = newWindow.document.createElement("button");
          printButton.innerHTML = "Print Report";
          printButton.className = "print-button";
          printButton.style.cssText =
            "position: fixed; top: 20px; right: 20px; padding: 8px 16px; background-color: #6e2ae8; color: white; border: none; border-radius: 4px; cursor: pointer; font-weight: bold;";
          printButton.addEventListener("click", () => {
            newWindow.print();
          });
          newWindow.document.body.appendChild(printButton);
        });
      } else {
        toast.warning(
          "Popup blocked. Please allow popups for this site to view the report."
        );
      }
    } catch (error) {
      console.error("Error downloading analysis:", error);
      toast.error("Failed to download analysis.");
    }
  };

  return (
    <Container fluid className="mt-4" style={{ backgroundColor: "#2a0f59" }}>
      <Container
        fluid
        style={{
          padding: "10px 10px",
        }}
      >
        <Row className="mb-2" style={{ margin: "10px" }}>
          <Col>
            <Card.Title as="h4" className="mb-0 text-primary">
              AI Competitor &amp; Market Research
            </Card.Title>
          </Col>
          <Col className="text-end">
            <Button
              disabled={loading}
              variant="outline-primary"
              className="dark-outline-button me-2"
              onClick={handleStartResearch}
            >
              <FontAwesomeIcon icon={faGlobe} className="me-2" />
              Start Research
            </Button>
            <Button
              disabled={loading}
              variant="outline-primary"
              className="dark-outline-button me-2"
              onClick={handleHistoryClick}
            >
              <FontAwesomeIcon icon={faHistory} className="me-2" />
              History
            </Button>

            <Button
              disabled={loading}
              variant="outline-primary"
              className="dark-outline-button me-2"
              onClick={() => window.location.reload()}
            >
              <FontAwesomeIcon icon={faRedo} className="me-2" />
              Start Over
            </Button>
          </Col>
        </Row>
      </Container>
      {researchResults && (
        <Row className="mb-3">
          <Col md={12}>
            <Card className="dark-main-card text-white">
              <Card.Header className="dark-card-header">
                <Card.Title as="h4" className="mb-0 text-primary">
                  Research Results
                </Card.Title>
              </Card.Header>
              <Card.Body>
                <p className="text-success">
                  <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                  Research generated successfully
                </p>
                <Button
                  variant="primary"
                  onClick={() =>
                    handleDownloadReport(researchResults.analysisId)
                  }
                >
                  <FontAwesomeIcon icon={faFilePdf} className="me-2" />
                  View or Print Report
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
      <Row className="g-3" style={{ position: "relative" }}>
        {loading ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              zIndex: 1000,
            }}
          >
            <div className="text-center">
              <Spinner
                animation="border"
                variant="primary"
                style={{ width: "3rem", height: "3rem" }}
              />
              <p className="mt-3 text-white">Research Running...</p>
            </div>
          </div>
        ) : null}

        <Col md={6}>
          {/* Left column with fixed 50/50 split */}
          <Card className="dark-main-card text-white mb-3">
            <Card.Header className="dark-card-header">
              <Card.Title as="h6" className="mb-0 text-primary">
                Competitors Analysis
              </Card.Title>
            </Card.Header>
            <Card.Body style={{ height: "280px", overflowY: "auto" }}>
              {competitorItems.length === 0 && (
                <div className="text-muted text-center py-3">
                  All competitor research items have been moved to the plan
                </div>
              )}

              {competitorItems.map((point, index) => {
                const labelPascalCase = point.label
                  .replace(/[^a-zA-Z0-9]+/g, "_")
                  .split("_")
                  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                  .join("");

                return (
                  <div
                    key={index}
                    className="dark-card p-2 mb-2"
                    draggable
                    onDragStart={(event) =>
                      handleDragStart(event, point, "competitor", index)
                    }
                    style={{ cursor: "grab" }}
                  >
                    <Form.Group className="mb-0 compact-form">
                      <Form.Label className="mb-1" style={{ color: "#d9d9d9" }}>
                        {point.label}:
                      </Form.Label>
                      <Form.Control
                        title={point.placeholder}
                        as="textarea"
                        className="dark-input"
                        placeholder={point.placeholder}
                        value={formData[labelPascalCase] || ""}
                        style={{
                          width: "100%",
                          minHeight: "60px",
                          resize: "none",
                        }}
                        data-item-index={index}
                        data-item-type="competitor"
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            [labelPascalCase]: e.target.value,
                          })
                        }
                      />
                    </Form.Group>
                  </div>
                );
              })}
            </Card.Body>
          </Card>

          <Card className="dark-main-card text-white">
            <Card.Header className="dark-card-header">
              <Card.Title as="h6" className="mb-0 text-primary">
                Market Research Points
              </Card.Title>
            </Card.Header>
            <Card.Body style={{ height: "280px", overflowY: "auto" }}>
              <>
                {marketItems.length === 0 && (
                  <div className="text-muted text-center py-3">
                    All market research items have been moved to the plan
                  </div>
                )}

                {marketItems.map((point, index) => {
                  const labelPascalCase = point.label
                    .replace(/[^a-zA-Z0-9]+/g, "_")
                    .split("_")
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join("");

                  return (
                    <div
                      key={index}
                      className="dark-card p-2 mb-2"
                      draggable
                      onDragStart={(event) =>
                        handleDragStart(event, point, "market", index)
                      }
                      style={{ cursor: "grab" }}
                    >
                      <Form.Group className="mb-0 compact-form">
                        <Form.Label
                          className="mb-1"
                          style={{ color: "#d9d9d9" }}
                        >
                          {point.label}:
                        </Form.Label>
                        <Form.Control
                          as="textarea"
                        title={point.placeholder}
                          className="dark-input"
                          placeholder={point.placeholder}
                          value={formData[labelPascalCase] || ""}
                          style={{
                            width: "100%",
                            minHeight: "60px",
                            resize: "none",
                          }}
                          data-item-index={index}
                          data-item-type="market"
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              [labelPascalCase]: e.target.value,
                            })
                          }
                        />
                      </Form.Group>
                    </div>
                  );
                })}
              </>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          {/* Right Pane - Drag and Drop Area */}
          <Card
            className="dark-main-card text-white"
            style={{ height: "600px" }}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            <Card.Header className="dark-card-header">
              <Card.Title as="h6" className="mb-0 text-primary">
                Research Plan Elements ({draggedItems.length})
              </Card.Title>
            </Card.Header>

            <Card.Body style={{ height: "540px", overflowY: "auto" }}>
              {draggedItems.length === 0 && (
                <div className="text-muted text-center p-5">
                  <p>Drag research elements here to add them to your plan</p>
                  <div className="border border-secondary rounded p-3 mt-2">
                    Drop Zone
                  </div>
                </div>
              )}

              {draggedItems.map((item, index) => (
                <div key={index} className="dark-card-alt p-3 mb-2">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <span className="d-block text-primary mb-1">
                        {item.label}
                      </span>
                      <span className="text-muted small d-block">
                        {item.originalType === "competitor"
                          ? "Competitor Research"
                          : "Market Research"}
                      </span>
                    </div>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      className="dark-outline-button ms-2"
                      onClick={() => handleRemove(index)}
                    >
                      &times;
                    </Button>
                  </div>
                  <div className="mt-2 p-2 bg-dark-accent rounded">
                    <small className="text-light" style={{ opacity: 0.9 }}>
                      {formData[labelToPascal(item.label)] || item.placeholder}
                    </small>
                  </div>
                </div>
              ))}
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <div className="tooltip-custom" id="help-tooltip-content">
        <div className="tooltip-inner">
          <h6>How to use this page:</h6>
          <ol>
            <li>Enter your competitor and market research information in the left panels</li>
            <li>Drag items from the left to the right "Research Plan Elements" panel</li>
            <li>Fill in all required information for each research point</li>
            <li>Click "Start Research" to generate your analysis</li>
            <li>Download your completed research as a PDF</li>
          </ol>
        </div>
      </div>
    </Container>
  );
};

const CompMarketResearchWithRoutes = () => {
  const { path, url } = useRouteMatch();

  return (
    <>
      <Route exact path={`${path}`}>
        <CompetitorAndMarketResearch />
      </Route>
      <Route path={`${path}/saved-items`}>
        <SavedItems onBack={() => window.history.back()} />
      </Route>
    </>
  );
};

export default CompMarketResearchWithRoutes;
